import type { IconReactionType } from "@/API";
import { Comment, type IconReaction, Message, Topic, type User } from "@/model";
import type { PayloadReaction } from "@/store";
import { useAppStore } from "@/store/app-store";
import { useCommentStore } from "@/store/comment-store";
import { useMessageStore } from "@/store/message-store";
import {
  type FindByComment,
  type FindByMessage,
  type FindByTopic,
  type FindReaction,
  useReactionStore,
} from "@/store/reaction-store";
import { useTopicStore } from "@/store/topic-store";

export class ReactionController {
  /**
   * リアクションを取得するためのパラメータを取得します
   * @param base 取得予定のベースとなるもの
   * @param container "parent": ベースの所属元を取得する "root": 話題を取得 指定なし: baseのまま
   */
  public static getReactionParam(
    base: Topic | Message | Comment,
    container?: "parent" | "root",
  ): FindReaction {
    if (base instanceof Topic) {
      const domainId = base.domainId;
      const topicId = base.id;
      return { domainId, topicId } as FindByTopic;
    } else if (base instanceof Message) {
      const domainId = base.domainId;
      const topicId = base.topicId;
      const messageId = base.id;
      switch (container) {
        case "parent":
          return { domainId, topicId } as FindByTopic;
        case "root":
          return { domainId, topicId } as FindByTopic;
        default:
          return { domainId, topicId, messageId } as FindByMessage;
      }
    } else if (base instanceof Comment) {
      const domainId = base.domainId;
      const topicId = base.topicId;
      const messageId = base.messageId;
      const commentId = base.id;
      switch (container) {
        case "parent":
          return { domainId, topicId, messageId } as FindByMessage;
        case "root":
          return { domainId, topicId } as FindByTopic;
        default:
          return { domainId, topicId, messageId, commentId } as FindByComment;
      }
    }
    return { domainId: "", topicId: "", messageId: "", commentId: "" };
  }

  /** リアクションの現在値を取得 */
  public reactionValue(
    target: Topic | Message | Comment,
    reactionType: IconReactionType,
    owner: User,
  ): boolean {
    const reaction = this.getReaction(target, reactionType);
    const value = reaction
      ? reaction.userList.some((u) => u.userId === owner.directId) || false
      : false;
    return value;
  }

  /** 話題ID から Topic を検索 */
  public findTopic(topicId: string): Topic | undefined {
    const domainId = useAppStore().domainId;
    const list = useTopicStore().get(domainId) as Topic[] | undefined;
    return list ? list.find((l) => l.id === topicId) : undefined;
  }

  /** 投稿ID から Message を検索 */
  public findMessage(messageId: string): Message | undefined {
    const app = useAppStore();
    const domainId = app.domainId;
    const topicId = app.topicId;
    const list = useMessageStore().get(domainId, topicId) as
      | Message[]
      | undefined;
    return list ? list.find((l) => l.id === messageId) : undefined;
  }

  /** 投稿ID、コメントID から Comment を検索 */
  public findComment(
    messageId: string,
    commentId: string,
  ): Comment | undefined {
    const topicId = useAppStore().topicId;
    const list = useCommentStore().get(topicId, messageId) as
      | Comment[]
      | undefined;
    return list ? list.find((l) => l.id === commentId) : undefined;
  }

  /** コメントのリアクションを取得します */
  public getReaction(
    target: Topic | Message | Comment | undefined,
    reactionType: IconReactionType,
  ): IconReaction | undefined {
    if (!target) return undefined;
    const type = ReactionController.getReactionParam(target);
    return useReactionStore().get(reactionType, type);
  }

  /**
   * リアクションの切り替え処理
   * @param value 現在値。無ければ検索します
   */
  public onReactionClicked(
    target: Topic | Message | Comment | undefined,
    reactionType: IconReactionType,
    owner: User,
    value?: boolean,
  ): void {
    if (!target) return;

    // 現在値
    if (value === undefined) {
      value = this.reactionValue(target, reactionType, owner);
    }
    const payload: PayloadReaction = {
      target,
      type: reactionType,
      value: !value,
    };
    useAppStore().setReaction(payload);
  }
}
